import { isAudiobookLibrary, NO_ENABLED_LIBRARIES } from './helpers';
import type { AuthSession, SessionAuthType } from './session';
import type { Library } from './types';

// Saved connection model: Flutter's SavedConnection (lib/domain/entities/
// saved_connection.dart) adapted to the web. Pure data + derived getters.

export type ConnectionAuthType = SessionAuthType;
export type ConnectionAuthState = 'ready' | 'reauthRequired';

export interface SavedConnectionLibrary {
  id: string;
  name: string;
  mediaType: string;
}

export interface ConnectionHeader {
  name: string;
  value: string;
}

/** Advanced options a browser can honour (TLS verification cannot be turned off on the web). */
export interface ConnectionOptions {
  /** Default request timeout in seconds (Flutter choices 5 / 10 / 15 / 30). */
  timeoutSeconds: number;
  /** Extra headers sent with API requests (not with cover/audio URLs, which can't carry headers). */
  customHeaders: ConnectionHeader[];
}

export const CONNECTION_TIMEOUT_CHOICES = [5, 10, 15, 30] as const;

export const DEFAULT_CONNECTION_OPTIONS: ConnectionOptions = { timeoutSeconds: 10, customHeaders: [] };

export interface SavedConnection {
  schemaVersion: 1;
  /** `abs:<userId>@<url>`. */
  id: string;
  /** Server display name at creation; never changed afterwards. */
  label: string;
  url: string;
  serverType: 'abs';
  serverName?: string;
  serverVersion?: string;
  nickname?: string;
  /** Deactivated connections stay listed but cannot be switched to. */
  isActive: boolean;
  authType: ConnectionAuthType;
  authState: ConnectionAuthState;
  username?: string;
  userId: string;
  /** `''` while re-authentication is required. Passwords are never stored. */
  accessToken: string;
  refreshToken?: string;
  /** The server's default library for this user (`userDefaultLibraryId`). */
  defaultLibraryId?: string;
  authFailureReason?: string;
  authFailureAt?: number;
  libraries: SavedConnectionLibrary[];
  /** Kept sorted, like Flutter's persisted set. */
  enabledLibraryIds: string[];
  selectedLibraryId?: string;
  options: ConnectionOptions;
  createdAt: number;
  updatedAt: number;
  lastUsedAt?: number;
}

export interface ConnectionsSnapshot {
  version: 1;
  activeId: string | null;
  connections: SavedConnection[];
}

export function connectionIdFor(userId: string, url: string): string {
  return `abs:${userId.trim()}@${url.trim()}`;
}

function hostOf(url: string): string | null {
  try {
    return new URL(url).host || null;
  } catch {
    return null;
  }
}

/** `serverName` → URL host → label → url → `Server`. */
export function resolvedServerName(c: Pick<SavedConnection, 'serverName' | 'url' | 'label'>): string {
  return c.serverName?.trim() || hostOf(c.url) || c.label.trim() || c.url.trim() || 'Server';
}

/** Nickname when set, else the server name. */
export function connectionDisplayTitle(c: SavedConnection): string {
  return c.nickname?.trim() || resolvedServerName(c);
}

export function enabledLibraryCount(c: Pick<SavedConnection, 'enabledLibraryIds' | 'libraries'>): number {
  if (c.enabledLibraryIds.length === 0) return 0;
  if (c.libraries.length === 0) return c.enabledLibraryIds.length;
  return c.libraries.filter((l) => c.enabledLibraryIds.includes(l.id)).length;
}

/** `No libraries discovered` / `<enabled> of <total> libraries enabled`. */
export function librariesSummary(c: Pick<SavedConnection, 'enabledLibraryIds' | 'libraries'>): string {
  if (c.libraries.length === 0) return 'No libraries discovered';
  return `${enabledLibraryCount(c)} of ${c.libraries.length} libraries enabled`;
}

export function requiresReauth(c: Pick<SavedConnection, 'authState' | 'accessToken'>): boolean {
  return c.authState === 'reauthRequired' || !c.accessToken;
}

/** A connection "Return to Library" and automatic fallbacks may switch to. */
export function isUsableConnection(c: SavedConnection): boolean {
  return c.isActive && !requiresReauth(c);
}

/**
 * The library the connection asks for (Flutter `_requestLibraryIdForConnection`):
 * the sentinel when every discovered library is disabled, the selection when
 * it is enabled, otherwise `''` so library resolution picks the default.
 */
export function requestLibraryIdFor(c: Pick<SavedConnection, 'enabledLibraryIds' | 'libraries' | 'selectedLibraryId'>): string {
  if (c.enabledLibraryIds.length === 0 && c.libraries.length > 0) return NO_ENABLED_LIBRARIES;
  const selected = c.selectedLibraryId?.trim();
  if (selected && (c.enabledLibraryIds.length === 0 || c.enabledLibraryIds.includes(selected))) return selected;
  return '';
}

const sorted = (ids: Iterable<string>): string[] => [...new Set([...ids].map((x) => x.trim()).filter(Boolean))].sort();

export function toConnectionLibraries(libraries: readonly Library[]): SavedConnectionLibrary[] {
  return libraries
    .map((l) => ({ id: String(l.id ?? '').trim(), name: String(l.name ?? '').trim(), mediaType: String(l.mediaType ?? '').trim() }))
    .filter((l) => l.id);
}

/**
 * `saveConnectionLibraries` rules: keep enabled ids that still exist; a
 * connection seeing libraries for the first time enables every audiobook
 * library; a selection that is no longer enabled moves to the first enabled.
 */
export function applyDiscoveredLibraries(c: SavedConnection, libraries: SavedConnectionLibrary[], enabled?: readonly string[]): SavedConnection {
  const ids = libraries.map((l) => l.id);
  let nextEnabled = sorted((enabled ?? c.enabledLibraryIds).filter((id) => ids.includes(id)));
  if (nextEnabled.length === 0 && ids.length > 0 && enabled === undefined && c.libraries.length === 0 && c.enabledLibraryIds.length === 0) {
    const audiobook = libraries.filter((l) => isAudiobookLibrary({ mediaType: l.mediaType })).map((l) => l.id);
    nextEnabled = sorted(audiobook.length > 0 ? audiobook : ids);
  }
  let selectedLibraryId = c.selectedLibraryId;
  if (selectedLibraryId && !nextEnabled.includes(selectedLibraryId)) selectedLibraryId = nextEnabled[0];
  return { ...c, libraries, enabledLibraryIds: nextEnabled, selectedLibraryId };
}

export function setLibraryEnabledIn(c: SavedConnection, libraryId: string, enabled: boolean): SavedConnection {
  const set = new Set(c.enabledLibraryIds);
  if (enabled) set.add(libraryId);
  else set.delete(libraryId);
  const enabledIds = sorted(set);
  let selectedLibraryId = c.selectedLibraryId;
  if (!enabled && selectedLibraryId === libraryId) selectedLibraryId = enabledIds[0];
  if (enabled && !selectedLibraryId) selectedLibraryId = libraryId;
  return { ...c, enabledLibraryIds: enabledIds, selectedLibraryId };
}

export function setAllLibrariesEnabledIn(c: SavedConnection, enabled: boolean): SavedConnection {
  if (!enabled) return { ...c, enabledLibraryIds: [], selectedLibraryId: undefined };
  const ids = sorted(c.libraries.map((l) => l.id));
  return { ...c, enabledLibraryIds: ids, selectedLibraryId: c.selectedLibraryId || ids[0] };
}

/** A connection record for a freshly authenticated session. */
export function connectionFromSession(session: AuthSession, now = Date.now()): SavedConnection {
  const host = hostOf(session.serverUrl) ?? session.serverUrl;
  return {
    schemaVersion: 1,
    id: session.connectionId ?? connectionIdFor(session.userId, session.serverUrl),
    label: host,
    url: session.serverUrl,
    serverType: 'abs',
    serverName: host,
    serverVersion: session.serverVersion,
    isActive: true,
    authType: session.authType ?? (session.refreshToken ? 'password' : 'apiKey'),
    authState: 'ready',
    username: session.username,
    userId: session.userId,
    accessToken: session.accessToken,
    refreshToken: session.refreshToken,
    defaultLibraryId: session.defaultLibraryId,
    libraries: [],
    enabledLibraryIds: [],
    options: { ...DEFAULT_CONNECTION_OPTIONS, customHeaders: [] },
    createdAt: now,
    updatedAt: now,
    lastUsedAt: now,
  };
}

/** The active session for a connection. */
export function sessionFromConnection(c: SavedConnection, extra: Partial<AuthSession> = {}): AuthSession {
  return {
    serverUrl: c.url,
    userId: c.userId,
    username: c.username ?? '',
    accessToken: c.accessToken,
    refreshToken: c.refreshToken,
    defaultLibraryId: c.defaultLibraryId,
    serverVersion: c.serverVersion,
    connectionId: c.id,
    authType: c.authType,
    ...extra,
  };
}

/** Reads a persisted record leniently (unknown fields dropped, missing ones defaulted, passwords never kept). */
export function parseConnection(raw: unknown): SavedConnection | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const url = typeof r.url === 'string' ? r.url.trim() : '';
  const userId = typeof r.userId === 'string' ? r.userId.trim() : '';
  if (!url || !userId) return null;
  const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
  const now = Date.now();
  const libraries = Array.isArray(r.libraries)
    ? (r.libraries as unknown[])
        .map((l) => (l && typeof l === 'object' ? (l as Record<string, unknown>) : null))
        .filter((l): l is Record<string, unknown> => !!l && typeof l.id === 'string' && !!l.id.trim())
        .map((l) => ({ id: String(l.id).trim(), name: String(l.name ?? '').trim(), mediaType: String(l.mediaType ?? '').trim() }))
    : [];
  const options = (r.options && typeof r.options === 'object' ? r.options : {}) as Partial<ConnectionOptions>;
  const authState: ConnectionAuthState = r.authState === 'reauthRequired' ? 'reauthRequired' : 'ready';
  return {
    schemaVersion: 1,
    id: str(r.id) ?? connectionIdFor(userId, url),
    label: str(r.label) ?? hostOf(url) ?? url,
    url,
    serverType: 'abs',
    serverName: str(r.serverName),
    serverVersion: str(r.serverVersion),
    nickname: str(r.nickname),
    isActive: r.isActive !== false,
    authType: r.authType === 'apiKey' ? 'apiKey' : 'password',
    authState,
    username: str(r.username),
    userId,
    accessToken: typeof r.accessToken === 'string' ? r.accessToken : '',
    refreshToken: str(r.refreshToken),
    defaultLibraryId: str(r.defaultLibraryId),
    authFailureReason: str(r.authFailureReason),
    authFailureAt: typeof r.authFailureAt === 'number' ? r.authFailureAt : undefined,
    libraries,
    enabledLibraryIds: sorted(Array.isArray(r.enabledLibraryIds) ? (r.enabledLibraryIds as unknown[]).filter((x): x is string => typeof x === 'string') : []),
    selectedLibraryId: str(r.selectedLibraryId),
    options: {
      timeoutSeconds: typeof options.timeoutSeconds === 'number' && options.timeoutSeconds > 0 ? options.timeoutSeconds : DEFAULT_CONNECTION_OPTIONS.timeoutSeconds,
      customHeaders: Array.isArray(options.customHeaders)
        ? options.customHeaders.filter((h): h is ConnectionHeader => !!h && typeof h.name === 'string' && typeof h.value === 'string')
        : [],
    },
    createdAt: typeof r.createdAt === 'number' ? r.createdAt : now,
    updatedAt: typeof r.updatedAt === 'number' ? r.updatedAt : now,
    lastUsedAt: typeof r.lastUsedAt === 'number' ? r.lastUsedAt : undefined,
  };
}
